import { useCallback, useState } from 'react';

import type { ExtractedItem } from '../../services/extraction.js';
import {
  buildCurveFromExtraction,
  buildRecurrenceFromExtraction,
  extractItems,
} from '../../services/extraction.js';
import type { CreateItemInput, ItemRepository, UserRepository } from '../../services/index.js';
import { useUIStore } from '../../stores/ui-store.js';
import { useUserStore } from '../../stores/user-store.js';
import { BrainDump } from './BrainDump.js';
import { ConversationalCapture } from './ConversationalCapture.js';
import { ItemConfirmation } from './ItemConfirmation.js';
import { ModeSelect } from './ModeSelect.js';

interface OnboardingFlowProps {
  itemRepo: ItemRepository;
  userRepo: UserRepository;
}

export function OnboardingFlow({ itemRepo, userRepo }: OnboardingFlowProps) {
  const { onboardingStep, onboardingMode, setOnboardingStep, setOnboardingMode } = useUIStore();
  const [extracted, setExtracted] = useState<ExtractedItem[]>([]);
  const [processing, setProcessing] = useState(false);

  const handleBraindumpSubmit = useCallback(
    async (text: string) => {
      setProcessing(true);
      try {
        const result = await extractItems({ mode: 'braindump', input: text });
        setExtracted(result.items);
        setOnboardingStep('confirmation');
      } catch {
        // Fallback: create single item from raw text
        setExtracted([
          {
            text,
            rawInput: text,
            curveDirection: 'cooling',
            provenance: {
              provider: 'local',
              model: 'none',
              promptVersion: '0',
              inputHash: '',
              confidence: 0,
            },
          },
        ]);
        setOnboardingStep('confirmation');
      } finally {
        setProcessing(false);
      }
    },
    [setOnboardingStep],
  );

  const handleConversationalExtracted = useCallback(
    (items: ExtractedItem[]) => {
      setExtracted(items);
      setOnboardingStep('confirmation');
    },
    [setOnboardingStep],
  );

  const handleConfirm = useCallback(
    async (items: ExtractedItem[]) => {
      for (const ext of items) {
        const input: CreateItemInput = {
          text: ext.text,
          rawInput: ext.rawInput,
          source:
            onboardingMode === 'braindump' ? 'onboarding-braindump' : 'onboarding-conversation',
          category: ext.category,
          consequence: ext.consequence,
          effort: ext.effort,
          curve: buildCurveFromExtraction(ext),
          recurrence: buildRecurrenceFromExtraction(ext),
        };
        await itemRepo.create(input);
      }
      await useUserStore.getState().completeOnboarding(userRepo);
      useUIStore.getState().setScreen('core');
    },
    [itemRepo, userRepo, onboardingMode],
  );

  const handleRemove = useCallback((index: number) => {
    setExtracted(prev => prev.filter((_, i) => i !== index));
  }, []);

  switch (onboardingStep) {
    case 'mode-select':
      return (
        <ModeSelect
          onSelectBraindump={() => {
            setOnboardingMode('braindump');
            setOnboardingStep('capture');
          }}
          onSelectConversational={() => {
            setOnboardingMode('conversational');
            setOnboardingStep('capture');
          }}
        />
      );
    case 'capture':
      if (onboardingMode === 'braindump') {
        return (
          <BrainDump
            onSubmit={text => {
              void handleBraindumpSubmit(text);
            }}
            isProcessing={processing}
          />
        );
      }
      return (
        <ConversationalCapture
          onExtracted={handleConversationalExtracted}
          isProcessing={processing}
          setProcessing={setProcessing}
        />
      );
    case 'confirmation':
      return (
        <ItemConfirmation
          items={extracted}
          onConfirm={items => {
            void handleConfirm(items);
          }}
          onRemove={handleRemove}
        />
      );
  }
}
